import { ApiResponse } from '@/types';
import { api } from './api';
import { Configuration, DropdownOption } from './configurationService';

export interface CreateConfigurationData {
  key: string;
  type: 'dropdown' | 'tools' | 'directories' | 'settings';
  category: string;
  name: string;
  description?: string;
  options?: DropdownOption[];
}

export interface UpdateConfigurationData {
  type?: 'dropdown' | 'tools' | 'directories' | 'settings';
  category?: string;
  name?: string;
  description?: string;
  options?: DropdownOption[];
}

export interface BulkOperation {
  key: string;
  action: 'toggleStatus' | 'updateCategory' | 'updateName' | 'updateDescription';
  data?: any;
}

export interface BulkOperationResult {
  key: string;
  success: boolean;
  message: string;
}

class DropdownAdminService {
  /**
   * Get all configurations for admin management
   */
  async getAllConfigurations(filters?: {
    type?: string;
    category?: string;
    search?: string;
  }): Promise<Configuration[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.search) params.append('search', filters.search);

      const response = await api.get<ApiResponse<Configuration[]>>(
        `/configurations?${params.toString()}`
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch configurations:', error);
      throw error;
    }
  }

  /**
   * Create new configuration
   */
  async createConfiguration(data: CreateConfigurationData): Promise<Configuration> {
    try {
      const response = await api.post<ApiResponse<Configuration>>('/configurations', data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to create configuration');
    } catch (error) {
      console.error('Failed to create configuration:', error);
      throw error;
    }
  }

  /**
   * Update existing configuration
   */
  async updateConfiguration(key: string, data: UpdateConfigurationData): Promise<Configuration> {
    try {
      const response = await api.put<ApiResponse<Configuration>>('/configurations', {
        key,
        ...data,
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to update configuration');
    } catch (error) {
      console.error('Failed to update configuration:', error);
      throw error;
    }
  }

  /**
   * Delete configuration
   */
  async deleteConfiguration(key: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(`/configurations/${key}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete configuration');
      }
    } catch (error) {
      console.error('Failed to delete configuration:', error);
      throw error;
    }
  }

  /**
   * Toggle configuration active status
   */
  async toggleConfigurationStatus(key: string): Promise<Configuration> {
    try {
      const response = await api.put<ApiResponse<Configuration>>(`/configurations/${key}/toggle`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to toggle configuration status');
    } catch (error) {
      console.error('Failed to toggle configuration status:', error);
      throw error;
    }
  }

  /**
   * Add option to dropdown configuration
   */
  async addDropdownOption(
    key: string,
    option: {
      value: string;
      label: string;
      metadata?: Record<string, any>;
      order?: number;
    }
  ): Promise<Configuration> {
    try {
      const response = await api.post<ApiResponse<Configuration>>(
        `/configurations/${key}/options`,
        option
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to add option');
    } catch (error) {
      console.error('Failed to add dropdown option:', error);
      throw error;
    }
  }

  /**
   * Update dropdown option
   */
  async updateDropdownOption(
    key: string,
    optionValue: string,
    updates: {
      label?: string;
      metadata?: Record<string, any>;
      isActive?: boolean;
      order?: number;
    }
  ): Promise<Configuration> {
    try {
      const response = await api.put<ApiResponse<Configuration>>(
        `/configurations/${key}/options/${encodeURIComponent(optionValue)}`,
        updates
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to update option');
    } catch (error) {
      console.error('Failed to update dropdown option:', error);
      throw error;
    }
  }

  /**
   * Delete dropdown option
   */
  async deleteDropdownOption(key: string, optionValue: string): Promise<Configuration> {
    try {
      const response = await api.delete<ApiResponse<Configuration>>(
        `/configurations/${key}/options/${encodeURIComponent(optionValue)}`
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to delete option');
    } catch (error) {
      console.error('Failed to delete dropdown option:', error);
      throw error;
    }
  }

  /**
   * Reorder dropdown options
   */
  async reorderDropdownOptions(key: string, optionValues: string[]): Promise<Configuration> {
    try {
      const response = await api.put<ApiResponse<Configuration>>(`/configurations/${key}/reorder`, {
        optionValues,
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to reorder options');
    } catch (error) {
      console.error('Failed to reorder dropdown options:', error);
      throw error;
    }
  }

  /**
   * Get configuration categories
   */
  async getConfigurationCategories(): Promise<string[]> {
    try {
      const response = await api.get<ApiResponse<string[]>>('/configurations/categories');

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch configuration categories:', error);
      throw error;
    }
  }

  /**
   * Bulk update configurations
   */
  async bulkUpdateConfigurations(operations: BulkOperation[]): Promise<BulkOperationResult[]> {
    try {
      const response = await api.post<ApiResponse<BulkOperationResult[]>>('/configurations/bulk', {
        operations,
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to perform bulk update');
    } catch (error) {
      console.error('Failed to bulk update configurations:', error);
      throw error;
    }
  }

  /**
   * Get dropdown configurations only
   */
  async getDropdownConfigurations(filters?: {
    category?: string;
    search?: string;
  }): Promise<Configuration[]> {
    return this.getAllConfigurations({
      type: 'dropdown',
      ...filters,
    });
  }

  /**
   * Duplicate configuration with new key
   */
  async duplicateConfiguration(
    sourceKey: string,
    newKey: string,
    newName: string
  ): Promise<Configuration> {
    try {
      // First get the source configuration
      const configurations = await this.getAllConfigurations();
      const sourceConfig = configurations.find(config => config.key === sourceKey);

      if (!sourceConfig) {
        throw new Error('Source configuration not found');
      }

      // Create new configuration based on source
      const newConfigData: CreateConfigurationData = {
        key: newKey,
        type: sourceConfig.type,
        category: sourceConfig.category,
        name: newName,
        description: `Copy of ${sourceConfig.name}`,
        options: sourceConfig.options ? [...sourceConfig.options] : undefined,
      };

      return await this.createConfiguration(newConfigData);
    } catch (error) {
      console.error('Failed to duplicate configuration:', error);
      throw error;
    }
  }

  /**
   * Import configuration from JSON
   */
  async importConfiguration(configData: CreateConfigurationData): Promise<Configuration> {
    try {
      return await this.createConfiguration(configData);
    } catch (error) {
      console.error('Failed to import configuration:', error);
      throw error;
    }
  }

  /**
   * Export configuration to JSON
   */
  exportConfiguration(configuration: Configuration): string {
    const exportData = {
      key: configuration.key,
      type: configuration.type,
      category: configuration.category,
      name: configuration.name,
      description: configuration.description,
      options: configuration.options,
      tools: configuration.tools,
      directories: configuration.directories,
      settings: configuration.settings,
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Validate configuration key format
   */
  validateConfigurationKey(key: string): { isValid: boolean; error?: string } {
    if (!key) {
      return { isValid: false, error: 'Key is required' };
    }

    if (key.length < 1 || key.length > 100) {
      return { isValid: false, error: 'Key must be between 1 and 100 characters' };
    }

    if (!/^[a-z0-9-_]+$/.test(key)) {
      return {
        isValid: false,
        error: 'Key can only contain lowercase letters, numbers, hyphens, and underscores',
      };
    }

    return { isValid: true };
  }

  /**
   * Generate unique key suggestion based on name
   */
  generateKeyFromName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
}

// Export singleton instance
export const dropdownAdminService = new DropdownAdminService();
